import { Button, Chip } from "@heroui/react";
import { FaCheck } from "react-icons/fa6";

interface Step {
  name: string;
  description: string;
  longDescription: string;
  hideTitle?: boolean;
}

interface StepperProps {
  steps: Step[];
  activeStep: number;
  changeStep: (step: number) => void;
  colorScheme?: "elastic" | "primary";
  completedSteps?: number[];
}

export function Stepper({
  steps,
  activeStep,
  changeStep,
  colorScheme = "elastic",
  completedSteps = [],
}: StepperProps) {
  const isPrimary = colorScheme === "primary";

  // цветовые классы для elastic
  const elasticActiveBg = "bg-elastic-50 dark:bg-elastic-900/20";
  const elasticActiveBorder = "border-elastic-500";
  const elasticCompletedBorder = "border-elastic-300";
  const elasticButtonBg = "bg-elastic-500 hover:!bg-elastic-600";
  const elasticChipBg = "bg-elastic-500";

  // цветовые классы для primary
  const primaryActiveBg = "bg-primary-50 dark:bg-primary-900/20";
  const primaryActiveBorder = "border-primary-500";
  const primaryCompletedBorder = "border-primary-300";
  const primaryButtonBg = "bg-primary-500 hover:!bg-primary-600";
  const primaryChipBg = "bg-primary-500";

  // выбор классов в зависимости от схемы
  const activeBg = isPrimary ? primaryActiveBg : elasticActiveBg;
  const activeBorder = isPrimary ? primaryActiveBorder : elasticActiveBorder;
  const completedBorder = isPrimary
    ? primaryCompletedBorder
    : elasticCompletedBorder;
  const buttonBg = isPrimary ? primaryButtonBg : elasticButtonBg;
  const chipBg = isPrimary ? primaryChipBg : elasticChipBg;
  const buttonText = isPrimary ? "text-white" : "text-black";
  const chipText = isPrimary ? "text-white" : "text-black";

  return (
    <div className="flex flex-col gap-2">
      {steps.map((step, index) => {
        const isCompleted = completedSteps.includes(index);
        const isAccessible = isCompleted || index <= activeStep;

        return (
          <div
            key={index}
            className={`p-3 rounded-md transition-colors duration-200 ${
              index === activeStep
                ? `${activeBg} border-l-4 ${activeBorder}`
                : isCompleted || index < activeStep
                  ? `bg-gray-50 dark:bg-gray-800/30 border-l-4 ${completedBorder}`
                  : "bg-gray-50 dark:bg-gray-800/50 border-l-4 border-gray-300 dark:border-gray-600"
            }`}>
            <div className="flex flex-row gap-4 items-center">
              <div className="flex flex-col items-center">
                <Button
                  isIconOnly
                  size="sm"
                  variant="light"
                  onClick={() => changeStep(index)}
                  disabled={!isAccessible}
                  className={
                    isCompleted || index <= activeStep
                      ? `${buttonBg} ${buttonText}`
                      : ""
                  }>
                  {isCompleted || index < activeStep ? (
                    <FaCheck />
                  ) : (
                    <span className="text-sm font-medium">{index + 1}</span>
                  )}
                </Button>
              </div>
              <div className="flex flex-col gap-1 flex-1">
                <span
                  className={`font-medium ${isAccessible ? "text-foreground" : "text-gray-400"}`}>
                  {step.name}
                </span>
                <span
                  className={`text-sm ${isAccessible ? "text-gray-600" : "text-gray-400"}`}>
                  {step.description}
                </span>
              </div>
              {isCompleted && (
                <Chip
                  className={`${chipBg} ${chipText}`}
                  variant="flat"
                  size="sm">
                  Завершено
                </Chip>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
